import { isNonProductionDatabase } from './productionDatabase';

// Issue #263 — every password path in the app, server and client, fell back to production.
// APP_URL (read by the create-user Pages Function) and REACT_APP_APP_URL (its client twin)
// were both unset on Cloudflare Preview, so a recovery link issued by staging pointed at
// production's /set-password — an origin whose database never issued that token. Rule 13's
// shape, with the twist that the variable was never set on either environment and
// production was correct only by accident.
//
// The two exports below are deliberately different from each other, and the difference is
// the whole point of this file. A future session changing one should read the other first.

// Password paths resolve to the origin the person is standing on. Nothing to configure and
// nothing to forget: a Preview deployment issues a Preview link, production issues a
// production link, and the token is always redeemed against the database that minted it.
// Supabase's own redirect allowlist is the backstop — an origin missing from that list
// falls back to the project's Site URL rather than redirecting somewhere unvetted.
export function passwordRedirectUrl() {
  return `${window.location.origin}/set-password`;
}

// Outbound email is the explicit carve-out and must NOT use window.location.origin.
// These CTAs are rendered by whoever happened to trigger the notification and are then
// delivered to someone else entirely, so deriving the origin from the sender would bake a
// preview-branch URL into mail sent to real people. Email always points at one canonical,
// long-lived host. The env override stays so a future custom domain needs no code change.
export const CANONICAL_APP_URL =
  process.env.REACT_APP_APP_URL || 'https://dr-signs-tools.pages.dev';

// Issue #507, Session 79.7 — the email carve-out above was RIGHT about the
// danger and WRONG about the remedy, and this is the corrected rule. Read both.
//
// WHAT COLBY SAW: he opened a test email sent by the STAGING copy of Stede,
// pressed the button in it, and landed on the REAL site. The record named in
// the mail does not exist there, so the link cannot work — and worse, Session
// 80 is somebody other than Colby running the acceptance test. They would
// follow a link out of a test email and be standing in live Dallas data,
// signed in, with nothing on screen to say so.
//
// THE BUG IS NOT "WRONG DOMAIN", IT IS "WRONG DATABASE". A link is correct when
// it opens a copy of Stede backed by the same database that holds the record.
// That framing is what makes the rule below obvious rather than a judgement
// call, and it is why the test is on the DATABASE and not on the hostname.
//
// WHY THE ORIGINAL CARVE-OUT CANNOT SIMPLY BE REVERSED. Its objection was that
// deriving the origin from the sender would bake a preview-branch URL into mail
// sent to real people. That is still true and still matters — so production
// keeps the canonical host, unconditionally. What changed is that mail from a
// non-production deployment can no longer reach a real person at all: #506's
// staging mail guard redirects anything addressed outside a short list of
// Colby's own inboxes. The danger the carve-out protected against was removed
// rather than accepted.
//
// AND WHY NOT JUST CHANGE THE CLOUDFLARE VARIABLE, which is the obvious move
// and is wrong: `labelTargetUrl()` below reads the same constant, and a QR code
// printed during testing would then carry a staging address onto a label that
// outlives the test by years. One setting was serving two readers who want
// opposite answers. This splits them instead of picking a side.
//
// THE SERVER ALREADY DOES THIS, TWICE, and the client never got the memo:
// `functions/create-user.js` falls back to the deployment's own origin so a
// Preview invite redeems its token against staging, and
// `functions/quickbooks-callback.js` says outright that it uses the
// deployment's own origin because APP_URL points at production on both
// environments. This is the same rule arriving on the browser side.
function emailLinkBase() {
  // PRODUCTION ALWAYS GETS THE CANONICAL HOST. An email is durable — it sits in
  // an inbox for years — so it must not carry whichever hostname the sender
  // happened to be using. `dr-signs-tools.pages.dev` still answers today and
  // may not forever.
  if (!isNonProductionDatabase(process.env.REACT_APP_SUPABASE_URL)) return CANONICAL_APP_URL;

  // ANYWHERE ELSE, THE DEPLOYMENT THAT SENT IT. A staging tester gets staging, a
  // feature-branch Preview gets that branch. Both are backed by the database
  // the record actually lives in, which is the whole point.
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  // No browser to ask. Nothing renders an email outside one today, so this is
  // unreachable rather than a real case — but it falls back to today's
  // behaviour rather than returning something broken.
  return CANONICAL_APP_URL;
}

// The address an email button should open. Used by `deepLinkUrl()` for every
// notification CTA, and by the generic "Open Stede" fallback in notify.js.
//
// NOTE THE FAILURE DIRECTION, which is the same one the mail guard chose and
// for the same reason: an unknown or unrecognisable database URL is treated as
// production, so a bug in the detection preserves today's behaviour instead of
// sending a real employee to a staging link.
export function emailTargetUrl(path) {
  const base = emailLinkBase();
  const suffix = String(path || '');
  if (!suffix) return base;
  return `${base}${suffix.startsWith('/') ? '' : '/'}${suffix}`;
}

// Issue #320, Session 74z8 — the third case, and the one with the longest
// memory of the three.
//
// `printLabel.js` and `canonM5Export.js` both derived their QR targets from
// window.location.origin, so a label printed from `npm start` encoded
// http://localhost:3000 and a label printed from a feature-branch Preview
// encoded an origin that dies with the branch. The QR renders perfectly in
// both cases — visual inspection cannot catch this, only scanning can, and
// by then the label is on a shelf or on a vinyl sheet at a branch.
//
// This is the email carve-out's reasoning taken one step further. Email is
// read by someone other than the sender, so it cannot use the sender's
// origin. A printed label is read by someone else, somewhere else, possibly
// years later, by which time the origin it was printed from may not resolve
// at all. Same rule, stronger case.
//
// Deliberately routed through CANONICAL_APP_URL rather than duplicating the
// env lookup: a future domain change is then one edit in one file, which is
// the whole reason that constant carries an override.
export function labelTargetUrl(path) {
  const suffix = String(path || '');
  return `${CANONICAL_APP_URL}${suffix.startsWith('/') ? '' : '/'}${suffix}`;
}
